import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, ChevronRightIcon, BrainCircuitIcon, CalendarIcon, UsersIcon, MailIcon } from 'lucide-react';
import { Button } from '../components/Button';
export function Authentication() {
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [particles, setParticles] = useState<Array<{
    x: number;
    y: number;
    size: number;
    speed: number;
    opacity: number;
  }>>([]);
  useEffect(() => {
    // Create particles for the animation effect
    const newParticles = Array.from({
      length: 50
    }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 0.3 + 0.1,
      opacity: Math.random() * 0.5 + 0.3
    }));
    setParticles(newParticles);
    // Animate particles
    const interval = setInterval(() => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        y: particle.y - particle.speed > 0 ? particle.y - particle.speed : 100,
        opacity: (Math.sin(Date.now() / 1000 * particle.speed) + 1) / 2 * 0.5 + 0.2
      })));
    }, 50);
    return () => clearInterval(interval);
  }, []);
  return <div className="w-full h-screen flex flex-col md:flex-row overflow-hidden bg-[#1E2A38]" data-id="element-1336">
      {/* Left side - AI visual */}
      <div className="hidden md:flex md:w-1/2 bg-[#141E2A] relative overflow-hidden" data-id="element-1337">
        <div className="absolute inset-0 z-0" data-id="element-1338">
          {/* Particle animation */}
          {particles.map((particle, i) => <div key={i} className="absolute rounded-full bg-[#3B82F6]" style={{
          left: `${particle.x}%`,
          top: `${particle.y}%`,
          width: `${particle.size}px`,
          height: `${particle.size}px`,
          opacity: particle.opacity,
          boxShadow: `0 0 ${particle.size * 3}px ${particle.size}px rgba(59, 130, 246, 0.6)`
        }} data-id="element-1339" />)}
        </div>
        {/* Neural network lines */}
        <svg className="absolute inset-0 w-full h-full z-10 opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-1340">
          <path d="M10,30 Q30,5 50,30 T90,30" stroke="#C8F169" strokeWidth="0.2" fill="none" data-id="element-1341" />
          <path d="M10,50 Q30,25 50,50 T90,50" stroke="#C8F169" strokeWidth="0.2" fill="none" data-id="element-1342" />
          <path d="M10,70 Q30,45 50,70 T90,70" stroke="#C8F169" strokeWidth="0.2" fill="none" data-id="element-1343" />
          <path d="M30,10 Q55,30 30,50 T30,90" stroke="#3B82F6" strokeWidth="0.2" fill="none" data-id="element-1344" />
          <path d="M50,10 Q75,30 50,50 T50,90" stroke="#3B82F6" strokeWidth="0.2" fill="none" data-id="element-1345" />
          <path d="M70,10 Q95,30 70,50 T70,90" stroke="#3B82F6" strokeWidth="0.2" fill="none" data-id="element-1346" />
        </svg>
        {/* Content */}
        <div className="relative z-20 flex flex-col justify-center items-center w-full h-full p-12 text-white" data-id="element-1347">
          <div className="w-full max-w-md" data-id="element-1348">
            <div className="mb-8" data-id="element-1349">
              <div className="flex items-center justify-center mb-6" data-id="element-1350">
                <div className="relative w-32 h-32" data-id="element-1351">
                  <div className="absolute inset-0 bg-[#C8F169] opacity-20 rounded-full animate-pulse" data-id="element-1352"></div>
                  <div className="absolute inset-4 bg-[#C8F169] opacity-10 rounded-full animate-pulse" style={{
                  animationDelay: '0.5s'
                }} data-id="element-1353"></div>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-1354">
                    <BrainCircuitIcon className="h-16 w-16 text-[#C8F169]" data-id="element-1355" />
                  </div>
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-center" data-id="element-1356">
                AI-Powered Meeting Preparation
              </h2>
              <p className="text-[#F5F7FA] text-opacity-80 text-center mb-8" data-id="element-1357">
                Transform your meetings with AI-driven insights, attendee
                research, and business intelligence.
              </p>
            </div>
            <div className="space-y-6" data-id="element-1358">
              <div className="bg-[#1E2A38] bg-opacity-50 rounded-xl p-5 backdrop-blur-sm border border-[#F5F7FA] border-opacity-10" data-id="element-1359">
                <div className="flex items-start" data-id="element-1360">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1361">
                    <CalendarIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1362" />
                  </div>
                  <div data-id="element-1363">
                    <h4 className="font-medium text-[#F5F7FA]" data-id="element-1364">
                      Calendar Integration
                    </h4>
                    <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1365">
                      Automatically syncs with your schedule
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#1E2A38] bg-opacity-50 rounded-xl p-5 backdrop-blur-sm border border-[#F5F7FA] border-opacity-10" data-id="element-1366">
                <div className="flex items-start" data-id="element-1367">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1368">
                    <UsersIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1369" />
                  </div>
                  <div data-id="element-1370">
                    <h4 className="font-medium text-[#F5F7FA]" data-id="element-1371">
                      Attendee Intelligence
                    </h4>
                    <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1372">
                      Comprehensive profiles on every participant
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#1E2A38] bg-opacity-50 rounded-xl p-5 backdrop-blur-sm border border-[#F5F7FA] border-opacity-10" data-id="element-1373">
                <div className="flex items-start" data-id="element-1374">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1375">
                    <MailIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1376" />
                  </div>
                  <div data-id="element-1377">
                    <h4 className="font-medium text-[#F5F7FA]" data-id="element-1378">
                      One-Click Follow-up
                    </h4>
                    <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1379">
                      Generate summaries and action items automatically
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Futuristic glowing edges */}
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-[#C8F169] via-transparent to-[#3B82F6] opacity-50" data-id="element-1380"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#3B82F6] via-transparent to-[#C8F169] opacity-50" data-id="element-1381"></div>
      </div>
      {/* Right side - Authentication form */}
      <div className="flex flex-col w-full md:w-1/2 justify-center items-center p-8 md:p-12 bg-[#1E2A38]" data-id="element-1382">
        <div className="w-full max-w-md" data-id="element-1383">
          <div className="text-center mb-8" data-id="element-1384">
            <Link to="/" className="inline-block" data-id="element-1385">
              <div className="flex items-center justify-center" data-id="element-1386">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ov4W5y1TwYUNeHUCdRzqBe/Prepz.io_Logo_2.png" alt="Prepz.io Logo" className="h-24 w-auto" data-id="element-1387" />
                <span className="ml-2 text-2xl font-bold text-[#F5F7FA]" data-id="element-1388">
                  Prepz.io
                </span>
              </div>
            </Link>
            <h1 className="mt-6 text-2xl font-bold text-[#F5F7FA]" data-id="element-1389">
              {authMode === 'login' ? 'Welcome Back' : 'Create Your Account'}
            </h1>
            <p className="mt-2 text-[#F5F7FA] text-opacity-70" data-id="element-1390">
              {authMode === 'login' ? 'Sign in to access your AI-powered meeting briefs' : 'Join thousands of professionals using AI to master meetings'}
            </p>
          </div>
          {/* Auth mode tabs */}
          <div className="flex mb-8 bg-[#141E2A] rounded-lg p-1" data-id="element-1391">
            <button className={`flex-1 py-2 px-4 rounded-md transition-all ${authMode === 'login' ? 'bg-[#3B82F6] text-white' : 'text-[#F5F7FA] text-opacity-70 hover:text-opacity-100'}`} onClick={() => setAuthMode('login')} data-id="element-1392">
              Log In
            </button>
            <button className={`flex-1 py-2 px-4 rounded-md transition-all ${authMode === 'signup' ? 'bg-[#3B82F6] text-white' : 'text-[#F5F7FA] text-opacity-70 hover:text-opacity-100'}`} onClick={() => setAuthMode('signup')} data-id="element-1393">
              Sign Up
            </button>
          </div>
          {/* Auth form */}
          <form className="space-y-4" data-id="element-1394">
            {authMode === 'signup' && <div className="grid grid-cols-2 gap-4" data-id="element-1395">
                <div data-id="element-1396">
                  <label htmlFor="firstName" className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-1397">
                    First Name
                  </label>
                  <input type="text" id="firstName" className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder="Enter first name" data-id="element-1398" />
                </div>
                <div data-id="element-1399">
                  <label htmlFor="lastName" className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-1400">
                    Last Name
                  </label>
                  <input type="text" id="lastName" className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder="Enter last name" data-id="element-1401" />
                </div>
              </div>}
            <div data-id="element-1402">
              <label htmlFor="email" className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-1403">
                Email Address
              </label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder="you@example.com" data-id="element-1404" />
            </div>
            <div data-id="element-1405">
              <div className="flex justify-between items-center mb-1" data-id="element-1406">
                <label htmlFor="password" className="block text-sm font-medium text-[#F5F7FA]" data-id="element-1407">
                  Password
                </label>
                {authMode === 'login' && <a href="#forgot" className="text-sm text-[#C8F169] hover:underline" data-id="element-1408">
                    Forgot password?
                  </a>}
              </div>
              <div className="relative" data-id="element-1409">
                <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 pr-10 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder={authMode === 'login' ? 'Enter password' : 'Create password'} data-id="element-1410" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-1411">
                  {showPassword ? <EyeOffIcon className="h-5 w-5 text-[#F5F7FA] text-opacity-60" data-id="element-1412" /> : <EyeIcon className="h-5 w-5 text-[#F5F7FA] text-opacity-60" data-id="element-1413" />}
                </button>
              </div>
            </div>
            {authMode === 'signup' && <div data-id="element-1414">
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1 text-[#F5F7FA]" data-id="element-1415">
                  Confirm Password
                </label>
                <input type="password" id="confirmPassword" className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50" placeholder="Confirm password" data-id="element-1416" />
              </div>}
            {authMode === 'login' && <div className="flex items-center" data-id="element-1417">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 rounded border-[#F5F7FA] border-opacity-30 text-[#C8F169] focus:ring-[#C8F169] focus:ring-opacity-50 bg-[#141E2A]" data-id="element-1418" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-[#F5F7FA] text-opacity-80" data-id="element-1419">
                  Remember me
                </label>
              </div>}
            <button type="submit" className="w-full bg-[#3B82F6] hover:bg-opacity-90 text-white font-medium py-2 px-4 rounded-lg transition-colors relative overflow-hidden group" data-id="element-1420">
              <span className="relative z-10" data-id="element-1421">
                {authMode === 'login' ? 'Log In' : 'Create Account'}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1422"></div>
            </button>
          </form>
          <div className="mt-6" data-id="element-1423">
            <div className="relative" data-id="element-1424">
              <div className="absolute inset-0 flex items-center" data-id="element-1425">
                <div className="w-full border-t border-[#F5F7FA] border-opacity-10" data-id="element-1426"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-1427">
                <span className="px-2 bg-[#1E2A38] text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1428">
                  Or continue with
                </span>
              </div>
            </div>
            <div className="mt-6" data-id="element-1429">
              <button type="button" className="w-full flex items-center justify-center bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 text-[#F5F7FA] font-medium py-2 px-4 rounded-lg hover:bg-opacity-90 transition-colors" data-id="element-1430">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" data-id="element-1431">
                  <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" data-id="element-1432" />
                </svg>
                Continue with Google
              </button>
              <div className="mt-6 text-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1433">
                {authMode === 'login' ? <>
                    Don't have an account?{' '}
                    <button onClick={() => setAuthMode('signup')} className="text-[#C8F169] hover:underline" data-id="element-1434">
                      Sign up
                    </button>
                  </> : <>
                    Already have an account?{' '}
                    <button onClick={() => setAuthMode('login')} className="text-[#C8F169] hover:underline" data-id="element-1435">
                      Log in
                    </button>
                  </>}
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-1436">
            <p className="text-xs text-[#F5F7FA] text-opacity-50" data-id="element-1437">
              By continuing, you agree to Prepz.io's{' '}
              <Link to="/privacy" className="text-[#C8F169] hover:underline" data-id="element-1438">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="#terms" className="text-[#C8F169] hover:underline" data-id="element-1439">
                Terms of Service
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
}